/**
 * Audience Context
 * Determines whether the current user is viewing the teen (student) or adult curriculum
 * based on the URL path prefix. Provides audience-aware helpers for routing and lesson IDs.
 */

import { createContext, useContext, useMemo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

export type Audience = 'teen' | 'adult';

interface AudienceContextValue {
  audience: Audience;
  isAdult: boolean;
  /** URL path prefix: '/financial-literacy' or '/adult-financial-literacy' */
  basePath: string;
  /** Offset lesson ID for API calls (adults use 101-132, teens use 1-32) */
  getApiLessonId: (lessonNumber: number) => number;
  /** Build a lesson URL for the current audience */
  lessonPath: (lessonNumber: number) => string;
  /** Build a phase study guide URL */
  studyGuidePath: (phaseId: number) => string;
  /** Build a phase test URL */
  phaseTestPath: (phaseId: number) => string;
  /** Build the final exam URL */
  finalExamPath: () => string;
}

const AudienceContext = createContext<AudienceContextValue | null>(null);

const ADULT_PATH_PREFIX = '/adult-financial-literacy';
const TEEN_PATH_PREFIX = '/financial-literacy';
const ADULT_LESSON_ID_OFFSET = 200;

export function AudienceProvider({ children }: { children: ReactNode }) {
  const location = useLocation();

  const value = useMemo<AudienceContextValue>(() => {
    const isAdult = location.pathname.startsWith(ADULT_PATH_PREFIX);
    const audience: Audience = isAdult ? 'adult' : 'teen';
    const basePath = isAdult ? ADULT_PATH_PREFIX : TEEN_PATH_PREFIX;

    return {
      audience,
      isAdult,
      basePath,
      getApiLessonId: (lessonNumber: number) =>
        isAdult ? lessonNumber + ADULT_LESSON_ID_OFFSET : lessonNumber,
      lessonPath: (lessonNumber: number) =>
        `${basePath}/lesson/${lessonNumber}`,
      studyGuidePath: (phaseId: number) =>
        `${basePath}/phase/${phaseId}/study-guide`,
      phaseTestPath: (phaseId: number) =>
        `${basePath}/phase/${phaseId}/test`,
      finalExamPath: () =>
        `${basePath}/final-exam`,
    };
  }, [location.pathname]);

  return (
    <AudienceContext.Provider value={value}>
      {children}
    </AudienceContext.Provider>
  );
}

export function useAudience(): AudienceContextValue {
  const context = useContext(AudienceContext);
  if (!context) {
    throw new Error('useAudience must be used within an AudienceProvider');
  }
  return context;
}

export { ADULT_PATH_PREFIX, TEEN_PATH_PREFIX, ADULT_LESSON_ID_OFFSET };
